/**
 * Efeitos de fundo da câmera: o catálogo (sem efeito, desfoque leve e forte, e quatro imagens), a escolha lembrada
 * entre chamadas e a checagem de suporte. Sem React: o `BackgroundEffectProvider` guarda o estado e o
 * `backgroundEffect` aplica o efeito na trilha da câmera.
 */

import { CUSTOM_BACKGROUND_ID } from './customBackground';

export const BACKGROUND_STORAGE_KEY = 'twc.videoCall.background';

/**
 * `mode` é o que o `BackgroundProcessor` do LiveKit recebe. As imagens são 1280x720 em `public/video-backgrounds`
 * (o nome leva um número: o nginx as guarda por um ano, então trocar uma imagem pede um nome novo).
 * `caption` é o rótulo curto que aparece embaixo do quadradinho; `label` é o nome acessível completo.
 */
export const BACKGROUND_OPTIONS = Object.freeze([
    { id: 'none', label: 'Sem efeito', caption: 'Nenhum', mode: null },
    { id: 'blur', label: 'Desfocar o fundo, leve', caption: 'Leve', mode: { mode: 'background-blur', blurRadius: 10 } },
    { id: 'blur-strong', label: 'Desfocar o fundo, forte', caption: 'Forte', mode: { mode: 'background-blur', blurRadius: 28 } },
    { id: 'image-azul', label: 'Fundo azul', caption: 'Azul', mode: { mode: 'virtual-background', imagePath: '/video-backgrounds/azul-1.jpg' } },
    { id: 'image-verde', label: 'Fundo verde', caption: 'Verde', mode: { mode: 'virtual-background', imagePath: '/video-backgrounds/verde-1.jpg' } },
    { id: 'image-areia', label: 'Fundo areia', caption: 'Areia', mode: { mode: 'virtual-background', imagePath: '/video-backgrounds/areia-1.jpg' } },
    { id: 'image-grafite', label: 'Fundo grafite', caption: 'Grafite', mode: { mode: 'virtual-background', imagePath: '/video-backgrounds/grafite-1.jpg' } },
]);

export const NO_BACKGROUND = 'none';

/** A opção do fundo personalizado (a imagem que a pessoa enviou): só existe depois do envio, e a URL é local (`blob:`). */
export function customBackgroundOption(url) {
    return { id: CUSTOM_BACKGROUND_ID, label: 'Seu fundo', caption: 'Seu fundo', mode: { mode: 'virtual-background', imagePath: url } };
}

/** A opção do catálogo com esse id (qualquer outra coisa vira "sem efeito"). */
export function backgroundOptionOf(id) {
    return BACKGROUND_OPTIONS.find((option) => option.id === id) ?? BACKGROUND_OPTIONS[0];
}

/**
 * A escolha lembrada neste navegador; sem ela (ou com um id que não existe mais), "sem efeito". O fundo personalizado
 * vale como escolha, mas só o provider sabe se a imagem ainda está guardada.
 */
export function readStoredBackground() {
    try {
        const id = window.localStorage.getItem(BACKGROUND_STORAGE_KEY);
        return id === CUSTOM_BACKGROUND_ID || BACKGROUND_OPTIONS.some((option) => option.id === id) ? id : NO_BACKGROUND;
    } catch {
        return NO_BACKGROUND;
    }
}

export function storeBackground(id) {
    try {
        if (id === NO_BACKGROUND) window.localStorage.removeItem(BACKGROUND_STORAGE_KEY);
        else window.localStorage.setItem(BACKGROUND_STORAGE_KEY, id);
    } catch {
        // Sem armazenamento (janela privativa): o efeito vale só nesta chamada.
    }
}

/**
 * O navegador roda o efeito? É a mesma checagem do `supportsBackgroundProcessors()` do `@livekit/track-processors`
 * (WebGL 2, OffscreenCanvas e WebCodecs, e uma das duas formas de processar quadros), repetida aqui para não baixar o
 * pacote só para decidir se o menu mostra a seção. Na dúvida (qualquer erro) é "não".
 */
export function supportsBackgroundEffects(win = typeof window === 'undefined' ? undefined : window) {
    try {
        if (!win) return false;
        const hasMedia = typeof win.OffscreenCanvas !== 'undefined' && typeof win.VideoFrame !== 'undefined' && typeof win.createImageBitmap !== 'undefined';
        const hasStreamProcessing = (typeof win.MediaStreamTrackGenerator !== 'undefined' && typeof win.MediaStreamTrackProcessor !== 'undefined')
            || (typeof win.HTMLCanvasElement !== 'undefined' && 'captureStream' in win.HTMLCanvasElement.prototype);
        return hasMedia && hasStreamProcessing && Boolean(win.document.createElement('canvas').getContext('webgl2'));
    } catch {
        return false;
    }
}
